import { IAnalyticsConfig, IGoogleAnalyticsEvent, ICustomTracker, IGmsAnalyticsEvent, IGAConfig, IGMSClickStreamConfig,
    IEventData, IGAExceptions, IGAPageView, IGA4Config } from '../index';
import { GoogleAnalytics4Service } from './googleAnalytics4.service';
/**
 * Generic functionality to track information
 */
class Analytics {

    gaConfig: IGAConfig;
    ga4Config: IGA4Config;
    clickStreamConfig: IGMSClickStreamConfig;

    /**
     * Creates default tracker
     * @param {IAnalyticsConfig} params -  Config for different analyticsvendors
     */
    setAnalyticsConfig(params: IAnalyticsConfig) {
        if (params?.GA) {
            this.gaConfig = params.GA;
            this.trackGaWindowError();
        }
        if (params?.GA4) {
            this.ga4Config = params.GA4;
        }
        if (params?.GMSClickStream) {
            this.clickStreamConfig = params.GMSClickStream;
        }

        // Creates default tracker
        if (window['ga'] && this.gaConfig?.enableVendor && this.gaConfig.gaId) {
            window['ga']('create', this.gaConfig.gaId, 'auto');
        }

        // Creates custom trackers for specified ga id
        if (window['ga'] && this.gaConfig?.enableVendor && this.gaConfig?.customTrackers?.length) {
            this.gaConfig.customTrackers.forEach((customTracker: ICustomTracker) => {
                window['ga']('create', customTracker.gaId, 'auto', customTracker.name);
            });
        }
        if (params?.GA4 && params?.GA4.gaId) {
            GoogleAnalytics4Service.initialize( params?.GA4.gaId, params?.GA4?.trackPageViews,
                params?.GA4?.trackWindowErrors, params?.GA4?.trackCustomEvents, params?.GA4?.customDimensions);
        }
    }

    /**
     * Track GA3 window error
     * @param {string} pageName
     */
    trackGaWindowError () {
        window.onerror = function (msg, url, lineNo, columnNo, error) {
            const message = [
                'Message: ' + msg,
                'URL: ' + url,
                'Line: ' + lineNo,
                'Column: ' + columnNo,
                'Error object: ' + JSON.stringify(error)
            ].join(' - ');
            window['ga']('send', 'exception', {
                'exDescription': message,
                'exFatal': false
                });
            return false;
        };
    }


    /**
     * Send page view to google analytics
     * @param {string} pageName
     */
    pageView(pageName: string) {
        if (window['ga'] && this.gaConfig?.enableVendor) {
            if (this.gaConfig.enableDefaultTracking) {
                window['ga']('send', 'pageview', pageName);
            }
            if (this.gaConfig.customTrackers.length) {
                this.gaConfig.customTrackers.forEach((customTracker: ICustomTracker) => {
                    if (customTracker.enableTracking) {
                        window['ga'](customTracker.name + '.send', 'pageview', pageName);
                    }
                });
            }
        }
    }

    /**
     * Send page view to google analytics
     * @param {string} pageName
     */
    ga4PageView(pageViewObj: IGAPageView) {
        if (window['gtag']) {
            GoogleAnalytics4Service.sendPageView(pageViewObj);
        }
    }

    /**
     * Check if value is object
     * @param value
     */
    isObject(val) {
        if (val === null) { return false; }
        return ((typeof val === 'function') || (typeof val === 'object'));
    }

    /**
     * Http Request for custom analytics url
     * @param {string} url
     */
    async request(url: string, req: RequestInit) {
        // Default options are marked with *
        const request: RequestInit = {
            method: req.method,
            cache: 'no-cache'  //// *default, no-cache, reload, force-cache, only-if-cached
        };
        if (req?.body ) {
            request.body = JSON.stringify(req.body);
        }
        const response = await fetch(url, request);
        return Promise.resolve(response);
    }


    /**
     * Generate query string for gms analytics using window variables
     */
    getQueryString() {
        let urlQueryString = '';
        if (window['__GMS_ABANDONMENT_VALUES__'] !== undefined) {
            if (window['__GMS_ABANDONMENT_VALUES__'].pushListId !== '') {
                urlQueryString = '&push_list_id=' + window['__GMS_ABANDONMENT_VALUES__'].pushListId;
            }
            if (window['__GMS_ABANDONMENT_VALUES__'].uniqueSig !== '') {
                urlQueryString = urlQueryString + '&signature=' + window['__GMS_ABANDONMENT_VALUES__'].uniqueSig;
            }
        }
        return urlQueryString;
    }

    /**
     * Track events to gms clickstream analytics - post api call
     * @param {IGmsAnalyticsEvent} gmsParams - Gms clickstream analytics params
     */
    trackGmsClickStreamPostEvent(gmsParams: IGmsAnalyticsEvent) {
        const urlQueryString = this.getQueryString();
        let isGmsEnabled = true;
        if (urlQueryString === '') {
            isGmsEnabled = false;
        }
        const label = gmsParams.label;
        label['property_code'] = gmsParams.hotelCode;

        if (window['__GMS_ABANDONMENT_VALUES__'] !== undefined) {
            if (window['__GMS_ABANDONMENT_VALUES__'].pushListId !== '') {
                label['push_list_id'] = window['__GMS_ABANDONMENT_VALUES__'].pushListId;
            }
            if (window['__GMS_ABANDONMENT_VALUES__'].uniqueSig !== '') {
                label['signature'] = window['__GMS_ABANDONMENT_VALUES__'].uniqueSig;
            }
        }

        if (gmsParams?.pageType && gmsParams?.category) {
            label['page_type'] = gmsParams.pageType;
            if (gmsParams.eventType) {
                label['action'] = gmsParams.eventType;
            }
            if (gmsParams.category) {
                label['category'] = gmsParams.category;
            }
        }

        if (isGmsEnabled) {
            const req: RequestInit = { method: 'POST', body: label };
            return this.request(this.clickStreamConfig.clickStreamUrl, req);
        } else {
            return Promise.resolve({});
        }

    }

    /**
     * Track events to gms clickstream analytics - GET api call
     * @param {IGmsAnalyticsEvent} gmsParams - Gms clickstream analytics params
     */
    trackGmsClickStreamEvent(gmsParams: IGmsAnalyticsEvent) {
        let url;
        let urlQueryString = this.getQueryString();
        let isGmsEnabled = true;
        if (urlQueryString === '') {
            isGmsEnabled = false;
        }
        const label = gmsParams.label;
        const isObj = this.isObject(label);

        if (gmsParams.pageType && gmsParams.category) {
            url = this.clickStreamConfig.clickStreamUrl + '?action=' + gmsParams.eventType + '&category=' + gmsParams.category + '&property_code=' + gmsParams.hotelCode + '&page_type=' + gmsParams.pageType;
            let flag = false;
            if (gmsParams.gmsId) {
                url += '&roomSeqNo=' + gmsParams.gmsId;
                flag = true;
            }

            if (label) {
                if (isObj) {
                    for (const key in label) {
                        if (key !== 'roomSeqNo') {
                            urlQueryString += '&' + key + '=' + label[key];
                        } else if (key === 'roomSeqNo' && flag !== true) {
                            urlQueryString += '&' + key + '=' + label[key];
                        }
                    }
                } else if (gmsParams.eventType.toLowerCase() === 'defaultlanguage') {
                    urlQueryString += '&language=' + label;
                } else if (gmsParams.eventType.toLowerCase() === 'defaultcurrency') {
                    urlQueryString += '&currency=' + label;
                } else {
                    urlQueryString += '&label=' + label;
                }
            }
            url += urlQueryString;
        } else {
            if (isObj) {
                for (const key in label) {
                    if (label.hasOwnProperty(key)) {
                        urlQueryString += '&' + key + '=' + label[key];
                    }
                }
            } else {
                urlQueryString += '&label=' + label;
            }
            url = this.clickStreamConfig.clickStreamUrl + '?action=' + gmsParams.eventType + '&property_code=' + gmsParams.hotelCode + '&page_type=' + gmsParams.eventType + urlQueryString;
        }

        if (isGmsEnabled) {
            const req: RequestInit = { method: 'GET' };
            return this.request(url, req);
        } else {
            return Promise.resolve({});
        }

    }


    /**
     * Track events to google analytics
     * @param {IGoogleAnalyticsEvent} gaParams - Google analytics params
     */
    trackGaEvent(gaParams: IGoogleAnalyticsEvent) {
        if (this.gaConfig?.enableDefaultTracking) {
            window['ga']('send', 'event', {
                eventCategory: gaParams.eventCategory,
                eventAction: gaParams.eventAction,
                eventLabel: gaParams.eventLabel
            });
        }

        if (this.gaConfig?.customTrackers.length) {
            this.gaConfig.customTrackers.forEach((customTracker: ICustomTracker) => {
                if (customTracker.enableTracking) {
                    window['ga'](customTracker.name + '.send', 'event', {
                        eventCategory: gaParams.eventCategory,
                        eventAction: gaParams.eventAction,
                        eventLabel: gaParams.eventLabel
                    });
                }
            });
        }
    }

    /**
     * Track events to multiple vendors
     * @param eventParams
     */
    trackEvent(eventParams: IEventData) {
        if (window['ga'] && this.gaConfig?.enableVendor && eventParams?.GAEvent) {
            this.trackGaEvent(eventParams.GAEvent);
        }
        if (this.clickStreamConfig?.enableVendor && eventParams?.GMSClickStreamEvent) {
            if (eventParams?.GMSClickStreamEvent?.method && eventParams.GMSClickStreamEvent.method.toLowerCase() === 'post') {
                this.trackGmsClickStreamPostEvent(eventParams.GMSClickStreamEvent);
            } else {
                this.trackGmsClickStreamEvent(eventParams.GMSClickStreamEvent);
            }
        }
        if (window['gtag'] && eventParams?.GAEvent) {
            const eventParameters =  {
                eventCategory: eventParams.GAEvent.eventCategory,
                eventLabel: eventParams.GAEvent.eventLabel,
            };
            if (!this.ga4Config?.trackPageViews) {
                eventParameters['page_path'] =  window.location.hash.replace('#', '');
            }
            GoogleAnalytics4Service.trackEvent({
                eventName: eventParams.GAEvent.eventAction,
                eventParams: eventParameters
            });
        }
    }

    /**
    * Capture exception
    *  @error
    */
    trackErrors(error: IGAExceptions) {
        if (window['gtag']) {
            GoogleAnalytics4Service.trackErrors(error);
        }
    }

    /**
     * Method to add extra context to all RUM events collected from your application
     * @param contextList
     */
    addGlobalContext(obj: any) {
        if (window['gtag']) {
            GoogleAnalytics4Service.addGlobalContext(obj);
        }
    }

    /**
     * Capture exception at window level
     */
    trackWindowErrors() {
        if (window['gtag']) {
            GoogleAnalytics4Service.trackWindowErrors();
        }
    }
}
export const AnalyticsService = new Analytics();
